import {networkOptimization} from '../../../../lib/api';
import {isFsaSupported, openSaveFilePicker, streamResponseToDownload} from '../../../../lib/download';
import {ProcessingFile} from '../types';

const HTTP_ERROR_MESSAGES: Record<number, string> = {
    404: 'No error report found for this file.',
    403: 'Access denied to this error report.',
    415: 'Unsupported error report format.',
};

const PROGRESS_THROTTLE_MS = 250;

export type PrepareDownloadResult =
    | {cancelled: true}
    | {cancelled: false; fileHandle: FileSystemFileHandle | undefined; suggestedName: string; usedFallback: boolean};

export type StreamDownloadOptions = {
    fileUploadId: number;
    fileHandle: FileSystemFileHandle | undefined;
    suggestedName: string;
    signal: AbortSignal;
    onProgress: (bytes: number) => void;
};

/**
 * Resolves a suggested filename from the ProcessingFile's name.
 * Strips the extension and appends "-error-report".
 */
const MIME_BY_EXTENSION: Record<string, {description: string; mime: string}> = {
    '.csv': {description: 'CSV file', mime: 'text/csv'},
    '.xlsx': {description: 'Excel file', mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'},
};

export function resolveErrorReportFilename(file: ProcessingFile | undefined, fileUploadId: number): {suggestedName: string; extension: string} {
    const baseName = file ? file.fileName.replace(/\.[^/.]+$/, '') : String(fileUploadId);
    const reportExtMatch = file?.errorReportFile?.match(/\.[^/.]+$/);
    const extension = reportExtMatch ? reportExtMatch[0].toLowerCase() : '.xlsx';
    return {suggestedName: `${baseName}-error-report${extension}`, extension};
}

/**
 * Phase 1: Opens the FSA Save dialog and returns the file handle.
 * Must be the first await in the call chain — browser requires it within
 * the user-gesture activation window.
 */
export async function prepareErrorReportDownload(
    file: ProcessingFile | undefined,
    fileUploadId: number
): Promise<PrepareDownloadResult> {
    const {suggestedName, extension} = resolveErrorReportFilename(file, fileUploadId);
    const knownType = MIME_BY_EXTENSION[extension];

    const fileHandle = isFsaSupported()
        ? await openSaveFilePicker({
            suggestedName,
            types: knownType
                ? [{description: knownType.description, accept: {[knownType.mime]: [extension]}}]
                : undefined,
        }).catch(() => undefined)
        : undefined;
    if (fileHandle === null) return {cancelled: true};

    return {cancelled: false, fileHandle, suggestedName, usedFallback: !fileHandle};
}

/**
 * Phase 2: Fetches the error report and streams it to the file handle (or memory fallback).
 * Throws with a user-friendly message on HTTP error.
 */
export async function streamErrorReport(options: StreamDownloadOptions): Promise<void> {
    const {fileUploadId, fileHandle, suggestedName, signal, onProgress} = options;

    const response = await networkOptimization.downloadErrorReport(fileUploadId, signal);
    if (!response.ok) {
        const message = HTTP_ERROR_MESSAGES[response.status] ?? 'Failed to download error report. Please try again.';
        throw new Error(message);
    }

    let lastProgressUpdate = 0;
    await streamResponseToDownload(
        response,
        suggestedName,
        fileHandle,
        (bytes) => {
            const now = Date.now();
            if (now - lastProgressUpdate >= PROGRESS_THROTTLE_MS) {
                lastProgressUpdate = now;
                onProgress(bytes);
            }
        }
    );
}
